import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AdminAgentInstance } from "@/queries/adminAgents";

interface DisableInstanceDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  instance: AdminAgentInstance;
  isLastEnabled: boolean;
  onConfirm: () => void;
  pending?: boolean;
}

export function DisableInstanceDialog({
  open,
  onOpenChange,
  instance,
  isLastEnabled,
  onConfirm,
  pending = false,
}: DisableInstanceDialogProps) {
  const { t } = useTranslation("admin-agents");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("dialogs.disableInstance.title")}</DialogTitle>
          <DialogDescription>
            {t("dialogs.disableInstance.subtitle", {
              family: instance.skill,
              instanceId: instance.instance_id,
            })}
          </DialogDescription>
        </DialogHeader>

        {isLastEnabled ? (
          <Alert variant="destructive">
            <AlertDescription>
              {t("dialogs.disableInstance.lastEnabledWarning")}
            </AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("dialogs.disableInstance.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={pending}
            data-testid="disable-instance-confirm"
          >
            {pending
              ? t("dialogs.disableInstance.confirming")
              : t("dialogs.disableInstance.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
